import React, { useEffect } from "react";
import {
  BodyTyp,
  CaptionsTyp,
} from "../../../../../components/Typography/Typography";
import {
  CANCEL_LABEL,
  CM_SHEET_LABEL,
  CONFIRM_LABEL,
  EDIT_REGISTRY_LABEL,
  IS_REQUIRED_LABEL,
  NAME_SURNAME_LABEL,
  ROLE_LABEL,
} from "../../../../../constants/Dictionary";
import { FontWeightEnum, Role } from "../../../../../types/AppTypes";
import { theme } from "../../../../../constants/StyledComponents";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../components/core/Select/Select";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { Modal } from "../../../../../components/core/Modal/Modal";
import { ReactComponent as CheckIcon } from "../../../../../icons/check.svg";
import { ReactComponent as CloseIcon } from "../../../../../icons/close.svg";
import { StyledConfigCollaboratorModal } from "./ConfigCollaboratorModal.styled";
import {
  ConfigCollaborator,
  UpdateConfigCollaboratorRequest,
} from "../../../../../types/api/configurations.type";
import { Loader } from "../../../../../components/core/Loader/Loader";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ToggleSwitch from "../../../../../components/core/ToggleSwitch/ToggleSwitch";
import { FormField } from "../../../../../components/core/FormField/FormField";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { findLabelFromValue } from "../../../../../utils/orgFunctions.utils";

type FormData = {
  role: string;
  enabledCmSheet?: boolean;
};

type ConfigCollaboratorModalProps = {
  collaborator: ConfigCollaborator;
  isOpen: boolean;
  closeModal: () => void;
  loading: boolean;
  onSave: (data: UpdateConfigCollaboratorRequest) => void;
};

export const ConfigCollaboratorModal = ({
  collaborator,
  isOpen,
  closeModal,
  loading,
  onSave,
}: ConfigCollaboratorModalProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);

  const schema = yup
    .object({
      role: yup.string().required(`${ROLE_LABEL} ${IS_REQUIRED_LABEL}`),
      enabledCmSheet: yup.boolean(),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, dirtyFields, errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    const body: UpdateConfigCollaboratorRequest = {
      ...(dirtyFields.role && {
        role: data.role as Role,
      }),
      ...(dirtyFields.enabledCmSheet && {
        enabledCmSheet: data.enabledCmSheet,
      }),
    };
    onSave(body);
  };

  useEffect(() => {
    reset({
      role: collaborator.role,
      enabledCmSheet: collaborator.enabledCmSheet,
    });
  }, [isOpen, collaborator, reset]);

  const roleOptions = [
    {
      value: Role.ADMIN,
      label: findLabelFromValue(orgSettings.userRoles.role, Role.ADMIN),
    },
    {
      value: Role.HR,
      label: findLabelFromValue(orgSettings.userRoles.role, Role.HR),
    },
    {
      value: collaborator.originalRole,
      label: findLabelFromValue(
        orgSettings.userRoles.role,
        collaborator.originalRole,
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      label={EDIT_REGISTRY_LABEL}
      rightButton={
        <Button
          icon={<CheckIcon />}
          label={CONFIRM_LABEL}
          disabled={!isDirty || loading}
          type="submit"
          form="editCollaborator"
        />
      }
      leftButton={
        <Button
          label={CANCEL_LABEL}
          onClick={closeModal}
          variant={ButtonVariants.SECONDARY}
          size={ButtonSizes.REGULAR}
          icon={<CloseIcon />}
        />
      }
      fullSize={false}
    >
      <Loader loading={loading}>
        <StyledConfigCollaboratorModal>
          <form
            id="editCollaborator"
            onSubmit={handleSubmit(onSubmit)}
            className="form-container"
          >
            <div>
              <CaptionsTyp color={theme.colors.grey_2}>
                {NAME_SURNAME_LABEL}
              </CaptionsTyp>
              <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                {collaborator.firstName} {collaborator.lastName}
              </BodyTyp>
            </div>
            <FormField error={errors.role?.message}>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Select
                    variant={SelectVariantEnum.SINGLE_SELECT}
                    label={ROLE_LABEL}
                    options={roleOptions}
                    {...field}
                  />
                )}
              />
            </FormField>
            <div className="toggle-container">
              <Controller
                name="enabledCmSheet"
                control={control}
                render={({ field }) => <ToggleSwitch {...field} />}
              />
              <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                {CM_SHEET_LABEL}
              </BodyTyp>
            </div>
          </form>
        </StyledConfigCollaboratorModal>
      </Loader>
    </Modal>
  );
};
